import SystemError from 'components/SystemError';
import { useFormik } from 'formik';
import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { newPasswordValidatonState } from 'utils/constants';
import passwordValidate from 'utils/passwordValidate';
import { newPasswordValidationType } from 'utils/types';

import {
  Button,
  designTokens,
  TextField,
  Typography,
} from '@project44/manifest-ds';
import { useTranslation } from '@project44/translations-utils';

import { useSetPassword } from '../../hooks/useSetPassword';
import EyeHidden from '../../resources/EyeHidden.svg';
import EyeOpen from '../../resources/EyeOpen.svg';
import SetPasswordInstructions from './SetPasswordInstructions';
import SetPasswordSuccess from './SetPasswordSuccess';
import { SetPasswordValidationSchema } from './SetPasswordValidationSchema';

const SetPassword = (): JSX.Element => {
  const { t } = useTranslation();

  const [passwordValidations, setPasswordValidations] =
    useState<newPasswordValidationType>(newPasswordValidatonState);
  const [setPasswordDataState, setPasswordDataHandler] = useSetPassword();
  const [showPassword, setShowPassword] = useState<boolean>(false);

  const history = useHistory();

  const location = useLocation();

  const isPasswordCreationSuccess = setPasswordDataState?.data !== undefined;

  const formik = useFormik({
    initialValues: {
      token: '',
      newPassword: '',
    },
    validationSchema: SetPasswordValidationSchema,
    validateOnChange: false,
    onSubmit: () => {
      const onSubmit = true;
      const validations = passwordValidate(
        formik.values.newPassword,
        onSubmit,
        passwordValidations,
      );
      setPasswordValidations(validations);
      const hasValidationPassed = Object.values(validations).every(
        (validation) => validation === 'pass',
      );
      hasValidationPassed &&
        setPasswordDataHandler({
          token: formik.values.token,
          newPassword: formik.values.newPassword,
          passwordConfirmation: formik.values.newPassword,
        });
    },
  });

  const changeHandler = (inputValue: string) => {
    const onSubmit = false;
    const validations = passwordValidate(
      inputValue,
      onSubmit,
      passwordValidations,
    );
    setPasswordValidations(validations);
    formik.errors.newPassword && formik.setErrors({ newPassword: '' });
    formik.setFieldValue('newPassword', inputValue);
  };

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const maybeBase64Token = searchParams.get('token');
    if (maybeBase64Token) {
      formik.setFieldValue('token', maybeBase64Token);
    } else {
      history.push('/login');
    }
  }, []);

  return isPasswordCreationSuccess ? (
    <SetPasswordSuccess />
  ) : (
    <>
      {setPasswordDataState?.error && (
        <SystemError statusCode={setPasswordDataState?.error?.statusCode} />
      )}
      <Typography
        variant="title"
        css={{
          marginBottom: designTokens.spaceMedium,
          color: designTokens.colorTextPrimary,
        }}
      >
        {t('setPasswordPage.setPasswordPageHeader')}
      </Typography>
      <form onSubmit={formik.handleSubmit} style={{ position: 'relative' }}>
        <TextField
          data-testid="new-password"
          aria-label="new password"
          css={{ marginBottom: designTokens.spaceSmall }}
          helperText={
            <SetPasswordInstructions
              validationError={formik.errors.newPassword}
              passwordValidations={passwordValidations}
            />
          }
          placeholder={t('setPasswordPage.newPasswordInputPlaceholder')}
          value={formik.values.newPassword}
          isDisabled={setPasswordDataState?.isLoading}
          inputProps={{
            type: showPassword ? 'text' : 'password',
            autoComplete: 'new-password',
          }}
          isRequired={true}
          validationState={
            formik.errors.newPassword ||
            Object.values(passwordValidations).includes('fail')
              ? 'invalid'
              : 'valid'
          }
          onChange={changeHandler}
        />
        <img
          style={{
            position: 'absolute',
            right: '0.6rem',
            top: '0.8rem',
            cursor: 'pointer',
          }}
          data-testid="show-password"
          onClick={() => setShowPassword(!showPassword)}
          src={!showPassword ? EyeOpen : EyeHidden}
        />
        <Button
          css={{
            width: '100%',
            marginTop: designTokens.spaceMedium,
          }}
          type="submit"
          variant="brand"
          isDisabled={setPasswordDataState?.isLoading}
        >
          {setPasswordDataState?.isLoading
            ? t('setPasswordPage.changingPassword')
            : t('setPasswordPage.changePassword')}
        </Button>
      </form>
    </>
  );
};

export default SetPassword;
